import cx from 'classnames';
import type { FC, ReactNode } from 'react';

import { RadioInput } from '@zen/DesignSystem';
import type { Optional } from '@zen/utils/typescript';

import type { RadioGroupOption } from './types';

interface Props {
  className?: string;
  columnCount?: number;
  disabled?: boolean;
  name: string;
  onChange: (value: string) => void;
  options: RadioGroupOption[];
  radioAlignment?: 'row' | 'column';
  radioType?: 'default' | 'tile';
  value: Optional<string>;
}

const RadioGroup: FC<Props> = (props) => {
  const { className, columnCount, options, radioAlignment, radioType = 'default', value, ...rest } = props;

  const columnCountSet: boolean = columnCount !== undefined;
  const isColumnAlignment: boolean = radioAlignment === 'column';
  const isTileRadioType: boolean = radioType === 'tile';

  const classNames: string = cx(
    {
      'flex-col': isColumnAlignment,
      'space-y-1.5': isColumnAlignment && !isTileRadioType,
      'space-x-2': !isColumnAlignment && !isTileRadioType && !columnCountSet,
      'divide-x': !isColumnAlignment && isTileRadioType && !columnCountSet,
      'divide-y': isColumnAlignment && isTileRadioType && !columnCountSet,
      [`grid grid-cols-${columnCount} gap-x-2 gap-y-2`]: columnCountSet,
      flex: !columnCountSet,
      'border border-solid border-grey-lighter rounded-sm divide-grey-lighter divide-solid': isTileRadioType
    },

    className
  );

  return (
    <div className={classNames} data-component="radio-group" data-testid="radio-group">
      {options.map(
        (option: RadioGroupOption, index: number): ReactNode => (
          <RadioInput
            {...rest}
            key={index}
            checked={option.value === value}
            description={option?.description}
            type={radioType}
            value={option.value}
          >
            {option.label}
          </RadioInput>
        )
      )}
    </div>
  );
};

export default RadioGroup;
